import { ApiConfig } from "../apiConfig";
import { ajax, AjaxConfig } from "rxjs/ajax";
import { Observable, throwError, of } from "rxjs";
import {
  catchError,
  map,
  finalize,
  switchMap,
  shareReplay,
  tap,
} from "rxjs/operators";
import { loaderController } from "../utils/loaderController";
import { session } from "../auth/session";
import { toast } from "react-toastify";

/* ---------- Local token helpers ---------- */
const ACCESS_KEY = "authToken";
const REFRESH_KEY = "refreshToken";

function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_KEY);
}
function setAccessToken(t: string | null) {
  if (t) sessionStorage.setItem(ACCESS_KEY, t);
  else sessionStorage.removeItem(ACCESS_KEY);
}

function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}
export function setRefreshToken(t: string | null) {
  if (t) sessionStorage.setItem(REFRESH_KEY, t);
  else sessionStorage.removeItem(REFRESH_KEY);
}

const CLIENT_PLATFORM = typeof navigator !== "undefined" ? "web" : "mobile";
const DEVICE_ID: string | undefined = undefined;

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiService {
  private get baseUrl(): string {
    return ApiConfig.getInstance().apiUrl;
  }
  private get virtualdirectoryUrl(): string {
    return ApiConfig.getInstance().VirtualdirectoryUrl;
  }

  /* ---------- Only one refresh at a time ---------- */
  private refreshInFlight$: Observable<string> | null = null;

  /* ---------- Refresh Access Token (send refreshToken) ---------- */
  private refreshAccessToken(): Observable<string> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      this.clearSession();
      // If you want to kick the user out immediately:
      try {
        if (typeof window !== "undefined") window.location.replace("/");
      } catch {}
      return throwError(() => new Error("No refresh token"));
    }

    const req$ = ajax<{
      responseCode: string;
      message: string;
      data?: { accessToken: string; refreshToken?: string };
    }>({
      url: `${this.baseUrl}/auth/refresh-token`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Client-Platform": CLIENT_PLATFORM,
        ...(DEVICE_ID ? { "X-Device-Id": DEVICE_ID } : {}),
      },
      body: { accessToken, refreshToken }, // <-- IMPORTANT
      withCredentials: true, // keep if you also use cookies
    }).pipe(
      map((res) => res.response ?? res), // normalize
      tap((body) => {
        const nextAccess = body?.data?.accessToken;
        const nextRefresh = body?.data?.refreshToken;
        if (!nextAccess)
          throw new Error("No accessToken returned from refresh");
        setAccessToken(nextAccess);
        if (nextRefresh) setRefreshToken(nextRefresh);
      }),
      map((body) => body.data!.accessToken),
      catchError((err) => {
        // hard logout on failed refresh
        this.clearSession();
        try {
          if (typeof window !== "undefined") window.location.replace("/");
        } catch {}
        return throwError(() => err);
      }),
      shareReplay(1),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
    );

    this.refreshInFlight$ = req$;
    return req$;
  }

  /* ---------- Helper: detect body-level unauthorized ---------- */
  private isBodyUnauthorized(payload: any): boolean {
    try {
      const code = String(
        payload?.responseCode ?? payload?.ResponseCode ?? "",
      ).toUpperCase();
      return code === "UNAUTHORIZED";
    } catch {
      return false;
    }
  }

  /* ---------- Core request ---------- */
  private rawRequest<T>(
    method: HttpMethod,
    path: string,
    body?: any,
    headers: Record<string, string> = {},
    responseType: XMLHttpRequestResponseType = "json",
    withCredentials = true,
    retryOn401 = true,
  ): Observable<T> {
    const token = getAccessToken();
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;

    const defaultHeaders: Record<string, string> = {
      ...(responseType === "json" && !isFormData
        ? { "Content-Type": "application/json" }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-Client-Platform": CLIENT_PLATFORM,
      ...(DEVICE_ID ? { "X-Device-Id": DEVICE_ID } : {}),
    };
    const mergedHeaders: Record<string, string> = {
      ...defaultHeaders,
      ...headers,
    };
    if (isFormData && "Content-Type" in mergedHeaders)
      delete (mergedHeaders as any)["Content-Type"];

    let cleanPath = path;
    const base = this.baseUrl.replace(/\/+$/, "");
    if (base.endsWith("/api") && cleanPath.startsWith("/api/")) {
      cleanPath = cleanPath.substring(4);
    }
    const url = path.startsWith("http")
      ? path
      : `${base}${cleanPath.startsWith("/") ? "" : "/"}${cleanPath}`;
    const config: AjaxConfig = {
      url,
      method,
      headers: mergedHeaders,
      responseType,
      withCredentials,
      ...(body !== undefined ? { body } : {}),
    };

    return ajax<T>(config).pipe(
      map((res) =>
        responseType === "json"
          ? (res.response as any as T)
          : (res.response as T),
      ),
      // If server uses 200 + responseCode: "UNAUTHORIZED", handle it here:
      switchMap((payload: any) => {
        if (responseType !== "json") return of(payload as T);
        if (!retryOn401) return of(payload as T);
        if (!this.isBodyUnauthorized(payload)) return of(payload as T);

        // try refresh then retry the original request once
        return this.refreshAccessToken().pipe(
          switchMap(() =>
            this.rawRequest<T>(
              method,
              path,
              body,
              headers,
              responseType,
              withCredentials,
              false,
            ),
          ),
        );
      }),
      /* ---------- Central non-OK responseCode toast ---------- */
      tap((payload: any) => {
        // Only inspect JSON responses that carry a responseCode field
        if (responseType !== "json") return;
        if (payload == null || typeof payload !== "object") return;
        const code = payload.responseCode ?? payload.ResponseCode;
        if (code === undefined) return; // response doesn't follow the pattern
        // UNAUTHORIZED is handled by the refresh flow above — don't double-toast
        if (String(code).toUpperCase() === "UNAUTHORIZED") return;
        if (String(code).toUpperCase() !== "OK") {
          const msg =
            payload.message ?? payload.Message ?? "Something went wrong.";
          toast.error(msg);
        }
      }),
      catchError((err) => {
        if (err && err.response && typeof err.response === "object") {
          if (!err.response.data) {
            err.response.data = err.response;
          }
        }
        const status = err?.status ?? err?.response?.status;
        const isRefreshCall = path.includes("/auth/refresh-token");
        if (status === 401 && retryOn401 && !isRefreshCall) {
          return this.refreshAccessToken().pipe(
            switchMap(() =>
              this.rawRequest<T>(
                method,
                path,
                body,
                headers,
                responseType,
                withCredentials,
                false,
              ),
            ),
          );
        }
        /* ---------- Central HTTP error toast ---------- */
        let errMsg = "A network or server error occurred.";
        if (err?.response && typeof err.response === "object") {
          errMsg =
            err.response.message ??
            err.response.Message ??
            err.response.error ??
            errMsg;
        } else if (err?.message && err.message !== "ajax error") {
          errMsg = err.message;
        } else if (status) {
          errMsg = `Server error (Status ${status}). Please try again later.`;
        } else {
          errMsg =
            "Could not connect to the server. Please check your network connection.";
        }
        if (responseType !== "blob") {
          toast.error(errMsg);
        }
        return throwError(() => err);
      }),
    );
  }

  /* ---------- Loader wrapper ---------- */
  private withLoader<T>(obs: Observable<T>): Observable<T> {
    loaderController.show();
    return obs.pipe(finalize(() => loaderController.hide()));
  }

  /* ---------- Public HTTP helpers ---------- */
  public get<T>(
    path: string,
    headers: Record<string, string> = {},
  ): Observable<T> {
    return this.withLoader(this.rawRequest<T>("GET", path, undefined, headers));
  }
  public post<T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
  ): Observable<T> {
    return this.withLoader(this.rawRequest<T>("POST", path, body, headers));
  }
  public put<T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
  ): Observable<T> {
    return this.withLoader(this.rawRequest<T>("PUT", path, body, headers));
  }
  public patch<T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
  ): Observable<T> {
    return this.withLoader(this.rawRequest<T>("PATCH", path, body, headers));
  }
  public delete<T>(
    path: string,
    body?: any,
    headers: Record<string, string> = {},
  ): Observable<T> {
    return this.withLoader(this.rawRequest<T>("DELETE", path, body, headers));
  }
  public postFormData<T>(
    path: string,
    body: FormData,
    headers: Record<string, string> = {},
  ) {
    return this.withLoader(this.rawRequest<T>("POST", path, body, headers));
  }
  public putFormData<T>(
    path: string,
    body: FormData,
    headers: Record<string, string> = {},
  ) {
    return this.withLoader(this.rawRequest<T>("PUT", path, body, headers));
  }
  public getimage<T>(
    path: string,
    headers: Record<string, string> = {},
    responseType: XMLHttpRequestResponseType = "blob",
  ): Observable<T> {
    return this.rawRequest<T>("GET", path, undefined, headers, responseType);
  }
  /**
   * Performs a POST request and expects a Blob (file) in response.
   */
  public postForBlob<T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
  ): Observable<T> {
    // The key difference is hardcoding 'blob' as the responseType in the rawRequest call
    return this.withLoader(
      this.rawRequest<T>("POST", path, body, headers, "blob"),
    );
  }

  public clearSession() {
    setAccessToken(null);
    setRefreshToken(null);
    session.clear();
  }

  public GetAPIURL() {
    try {
      return ApiConfig.getInstance().apiUrl;
    } catch {
      return "";
    }
  }
  public GetVirtualdirectoryUrl() {
    try {
      return ApiConfig.getInstance().VirtualdirectoryUrl;
    } catch {
      return "";
    }
  }

  public refreshNow(): Observable<{ accessToken: string }> {
    return this.refreshAccessToken().pipe(
      map((access) => ({ accessToken: access })),
    );
  }

  public logout(refreshToken?: string): Observable<void> {
    const body = refreshToken ? { refreshToken } : {};
    return this.post<{ responseCode: string; message: string }>(
      "/auth/logout",
      body,
    ).pipe(
      map(() => this.clearSession()),
      catchError(() => {
        this.clearSession();
        return of(void 0);
      }),
    );
  }
}

export const apiService = new ApiService();
